import { Popup, TWclassNames } from "./Div";
import { Link } from "react-router-dom";

/**
 * Shared button. Styling follows the Promang component kit
 * (public/docs/core/PROMANG_COMPONENTS.md §1 Buttons) via the `pm-btn*`
 * classes in src/index.css.
 *
 * styleType: "primary" (default) | "secondary" | "ghost" | "danger" |
 *            "minimal" (text-only) | "custom" (no kit styling).
 * size:      "md" (default, 42px) | "sm" (32px).
 */
const KIT_VARIANTS = ["primary", "secondary", "ghost", "danger"];

// A consumer base `bg-*` colour (optionally breakpoint-prefixed) must win over
// the kit gradient, which Tailwind's background-color utilities cannot cover.
// State-only variants such as `hover:bg-*` keep the gradient.
const HAS_BG_OVERRIDE = /(^|\s)((sm|md|lg|xl|2xl):)?bg-(?!none\b|opacity-)/;

export const PMButton = ({ styleType = "primary", size = "md", ...props }: any) => {
  const { children, onClick, type, className, tooltip, to, target, disabled, loading } =
    props;

  function styleTypeClass() {
    if (KIT_VARIANTS.includes(styleType)) {
      return TWclassNames(
        "pm-btn",
        `pm-btn--${styleType}`,
        size === "sm" ? "pm-btn--sm" : "",
        HAS_BG_OVERRIDE.test(className || "") ? "!bg-none !shadow-none" : "",
        disabled ? "is-disabled" : ""
      );
    }

    const baseClasses = disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer";
    if (styleType === "minimal") {
      return `bg-transparent hover:text-blue-400 text-xs ${baseClasses}`;
    }
    return baseClasses;
  }

  const content = loading ? (
    <span className="inline-flex items-center gap-2">
      <span className="pm-btn__spinner" aria-hidden="true" />
      {children}
    </span>
  ) : (
    children
  );

  if (type === "button" || type === "submit") {
    return (
      <Popup content={tooltip} textSize="md">
        <button
          onClick={disabled || type === "submit" ? undefined : onClick}
          type={type}
          disabled={disabled}
          aria-busy={loading || undefined}
          className={TWclassNames("flex w-full justify-center", styleTypeClass(), className)}
        >
          {content}
        </button>
      </Popup>
    );
  } else if (to) {
    return (
      <Popup content={tooltip} textSize="md">
        <Link
          target={target}
          to={disabled ? "#" : to}
          aria-disabled={disabled || undefined}
          className={TWclassNames("flex w-full justify-center", styleTypeClass(), className)}
        >
          {children}
        </Link>
      </Popup>
    );
  } else {
    return (
      <Popup content={tooltip} textSize="md">
        <div
          onClick={disabled ? undefined : onClick}
          className={TWclassNames(styleTypeClass(), "flex w-full justify-center", className)}
        >
          {content}
        </div>
      </Popup>
    );
  }
};
